import { Injectable } from '@angular/core';
import { Observable, of, tap, shareReplay, finalize } from 'rxjs';

interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  duration: number;
}

@Injectable({ providedIn: 'root' })
export class CacheService {
  private readonly cache = new Map<string, CacheEntry>();
  private readonly inFlight = new Map<string, Observable<any>>();
  private readonly DEFAULT_DURATION = 5 * 60 * 1000; // 5 minutes

  get<T>(key: string, source: Observable<T>, duration?: number): Observable<T> {
    const cached = this.cache.get(key);

    if (cached && Date.now() - cached.timestamp < cached.duration) {
      console.log(`[CacheService] HIT: "${key}"`);
      return of(cached.data as T);
    }

    // Return existing in-flight request to avoid duplicate API calls
    const existing = this.inFlight.get(key);
    if (existing) {
      console.log(`[CacheService] IN-FLIGHT: "${key}"`);
      return existing as Observable<T>;
    }

    console.log(`[CacheService] MISS: "${key}" — fetching from API`);
    const request$ = source.pipe(
      tap((data) => this.set(key, data, duration)),
      shareReplay(1),
      finalize(() => this.inFlight.delete(key))
    );

    this.inFlight.set(key, request$);
    return request$;
  }

  set<T>(key: string, data: T, duration?: number): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      duration: duration ?? this.DEFAULT_DURATION,
    });
  }

  has(key: string): boolean {
    const cached = this.cache.get(key);
    if (!cached) return false;

    if (Date.now() - cached.timestamp >= cached.duration) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  delete(key: string): void {
    this.cache.delete(key);
    this.inFlight.delete(key);
  }

  clear(): void {
    this.cache.clear();
    this.inFlight.clear();
  }
}
